/**
 * Layer 3: ViewerProfile
 *
 * Controls WHAT content is shown - content filtering, prioritization, and messaging tone.
 *
 * This layer determines which information surfaces to which audience.
 */

export type ViewerProfile =
  | 'public-view'      // General public - highlights, showcase work
  | 'recruiter-view'   // Recruiters/HR - experience, skills, achievements
  | 'technical-view'   // Engineers - technical depth, architecture, code
  | 'executive-view'   // C-level - business impact, leadership, strategy

export const VIEWER_PROFILES: ViewerProfile[] = [
  'public-view',
  'recruiter-view',
  'technical-view',
  'executive-view',
]

export interface ContentPriority {
  showTechnicalDetails: boolean
  showBusinessMetrics: boolean
  showLeadershipNarrative: boolean
  showPersonalProjects: boolean
  showProfessionalExperience: boolean
  emphasizeSkills: boolean
  emphasizeAchievements: boolean
  emphasizeImpact: boolean
}

export interface MessagingTone {
  formality: 'casual' | 'professional' | 'formal'
  technicality: 'layman' | 'intermediate' | 'expert'
  focus: 'breadth' | 'balanced' | 'depth'
}

export interface ViewerProfileConfig {
  id: ViewerProfile
  name: string
  description: string
  targetAudience: string
  contentPriority: ContentPriority
  messagingTone: MessagingTone
  visibleSections: string[]
  hiddenSections: string[]
}

export const VIEWER_PROFILE_CONFIGS: Record<ViewerProfile, ViewerProfileConfig> = {
  'public-view': {
    id: 'public-view',
    name: 'Public View',
    description: 'General public browsing - highlights and showcase work',
    targetAudience: 'General visitors, potential collaborators',
    contentPriority: {
      showTechnicalDetails: false,
      showBusinessMetrics: false,
      showLeadershipNarrative: false,
      showPersonalProjects: true,
      showProfessionalExperience: true,
      emphasizeSkills: false,
      emphasizeAchievements: true,
      emphasizeImpact: false,
    },
    messagingTone: {
      formality: 'casual',
      technicality: 'layman',
      focus: 'breadth',
    },
    visibleSections: ['about', 'projects', 'skills', 'contact'],
    hiddenSections: ['compensation', 'detailed-experience', 'certifications'],
  },
  'recruiter-view': {
    id: 'recruiter-view',
    name: 'Recruiter View',
    description: 'Recruiters and HR professionals - experience, skills, achievements',
    targetAudience: 'Recruiters, hiring managers, HR professionals',
    contentPriority: {
      showTechnicalDetails: false,
      showBusinessMetrics: true,
      showLeadershipNarrative: true,
      showPersonalProjects: false,
      showProfessionalExperience: true,
      emphasizeSkills: true,
      emphasizeAchievements: true,
      emphasizeImpact: true,
    },
    messagingTone: {
      formality: 'professional',
      technicality: 'intermediate',
      focus: 'balanced',
    },
    visibleSections: [
      'about',
      'experience',
      'skills',
      'achievements',
      'education',
      'certifications',
      'contact',
    ],
    hiddenSections: ['technical-deep-dives', 'code-samples'],
  },
  'technical-view': {
    id: 'technical-view',
    name: 'Technical View',
    description: 'Engineers and technical professionals - technical depth, architecture, code',
    targetAudience: 'Software engineers, technical leads, CTOs',
    contentPriority: {
      showTechnicalDetails: true,
      showBusinessMetrics: false,
      showLeadershipNarrative: false,
      showPersonalProjects: true,
      showProfessionalExperience: true,
      emphasizeSkills: true,
      emphasizeAchievements: false,
      emphasizeImpact: false,
    },
    messagingTone: {
      formality: 'casual',
      technicality: 'expert',
      focus: 'depth',
    },
    visibleSections: [
      'about',
      'projects',
      'technical-details',
      'architecture',
      'code-samples',
      'skills',
      'open-source',
      'contact',
    ],
    hiddenSections: ['business-metrics', 'leadership-narrative'],
  },
  'executive-view': {
    id: 'executive-view',
    name: 'Executive View',
    description: 'C-level executives - business impact, leadership, strategy',
    targetAudience: 'CEOs, CTOs, VPs, senior leadership',
    contentPriority: {
      showTechnicalDetails: false,
      showBusinessMetrics: true,
      showLeadershipNarrative: true,
      showPersonalProjects: false,
      showProfessionalExperience: true,
      emphasizeSkills: false,
      emphasizeAchievements: true,
      emphasizeImpact: true,
    },
    messagingTone: {
      formality: 'formal',
      technicality: 'intermediate',
      focus: 'breadth',
    },
    visibleSections: [
      'about',
      'experience',
      'business-impact',
      'leadership',
      'strategic-initiatives',
      'achievements',
      'contact',
    ],
    hiddenSections: ['technical-details', 'code-samples', 'personal-projects'],
  },
}
